import {
 useState, useMemo, useCallback, useEffect 
} from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Checkbox, FormControlLabel, Typography, TextField, InputAdornment, Divider, List, ListItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import LayersIcon from '@mui/icons-material/Layers';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from '../../common/components/LocalizationProvider';
import DraggableFilterPanel from './DraggableFilterPanel';
import { WIDGET_POSITIONS } from '../../common/theme/layout';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

// Chave do localStorage para persistir filtro
const STORAGE_KEY = 'geofenceFilterVisibleIds';

// Evento customizado para notificar mudanças no filtro
export const GEOFENCE_FILTER_CHANGE_EVENT = 'geofenceFilterChange';

/**
 * Recupera os IDs das geofences visíveis do localStorage
 * @returns {Set<number>|null} Set de IDs ou null se não houver filtro
 */
export const getVisibleGeofenceIds = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const ids = JSON.parse(stored);
      return new Set(ids);
    }
  } catch (e) {
    console.warn('Erro ao ler filtro de geofences:', e);
  }
  return null;
};

/**
 * Salva os IDs das geofences visíveis no localStorage
 * @param {Set<number>|null} ids Set de IDs ou null para mostrar todas
 */
const saveVisibleGeofenceIds = (ids) => {
  try {
    if (ids === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
    }
    // Dispara evento customizado para notificar MapGeofence
    window.dispatchEvent(new CustomEvent(GEOFENCE_FILTER_CHANGE_EVENT, {
      detail: { visibleIds: ids },
    }));
  } catch (e) {
    console.warn('Erro ao salvar filtro de geofences:', e);
  }
};

const useStyles = makeStyles()((theme, { tokens }) => ({
  content: {
    padding: theme.spacing(0.5, 1),
  },
  searchField: {
    '& .MuiInputBase-root': {
      fontSize: '0.8rem',
      height: 28,
      borderRadius: 8,
      backgroundColor: tokens.surface.glass,
      border: `1px solid ${tokens.border.subtle}`,
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
  },
  actionButton: {
    fontSize: '0.7rem',
    padding: theme.spacing(0.25, 0.5),
    minWidth: 'auto',
    borderRadius: 8,
    color: tokens.text.secondary,
    '&:hover': {
      backgroundColor: tokens.accent.subtle,
      color: tokens.accent.primary,
    },
  },
  list: {
    margin: 0,
    padding: 0,
  },
  listItem: {
    padding: theme.spacing(0.25, 0.5),
    minHeight: 28,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: tokens.surface.glassHover,
    },
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: `1px solid ${tokens.border.subtle}`,
    flexShrink: 0,
  },
  geofenceName: {
    fontSize: '0.8rem',
    color: tokens.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  checkbox: {
    padding: theme.spacing(0.25),
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.accent.primary,
    },
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: tokens.text.muted,
  },
}));

const GeofenceFilterPanel = ({ open, onClose, initialPosition }) => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ tokens });
  const theme = useTheme();
  const t = useTranslation();
  const geofences = useSelector((state) => state.geofences.items);

  // Estado local para geofences selecionadas (IDs)
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Estado para busca por nome
  const [searchQuery, setSearchQuery] = useState('');

  // Lista de geofences ordenada por nome
  const sortedGeofences = useMemo(() => Object.values(geofences).sort((a, b) => (a.name || '').localeCompare(b.name || '')), [geofences]);

  // Lista filtrada pela busca
  const filteredGeofences = useMemo(() => {
    if (!searchQuery.trim()) return sortedGeofences;
    const query = searchQuery.toLowerCase().trim();
    return sortedGeofences.filter((g) => (g.name || '').toLowerCase().includes(query));
  }, [sortedGeofences, searchQuery]);

  // Inicializar com valores do localStorage ou todas visíveis
  useEffect(() => {
    if (open) {
      const storedIds = getVisibleGeofenceIds();
      if (storedIds) {
        setSelectedIds(storedIds);
      } else {
        // Se não há filtro salvo, todas estão visíveis
        const allIds = Object.values(geofences)
          .filter((g) => !g.attributes?.hide)
          .map((g) => g.id);
        setSelectedIds(new Set(allIds));
      }
      setSearchQuery('');
    }
  }, [open, geofences]);

  const handleToggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // Salva automaticamente
      const allIds = new Set(sortedGeofences.map((g) => g.id));
      const isAllSelected = next.size === allIds.size && [...next].every((i) => allIds.has(i));
      if (isAllSelected) {
        saveVisibleGeofenceIds(null);
      } else {
        saveVisibleGeofenceIds(next);
      }
      return next;
    });
  }, [sortedGeofences]);

  const handleSelectAll = useCallback(() => {
    const allIds = new Set(sortedGeofences.map((g) => g.id));
    setSelectedIds(allIds);
    saveVisibleGeofenceIds(null);
  }, [sortedGeofences]);

  const handleSelectNone = useCallback(() => {
    setSelectedIds(new Set());
    saveVisibleGeofenceIds(new Set());
  }, []);

  // Extrai cor da geofence dos atributos ou usa cor padrão
  const getGeofenceColor = useCallback((geofence) => geofence.attributes?.color || theme.palette.primary.main, [theme]);

  const totalCount = sortedGeofences.length;
  const visibleCount = selectedIds.size;

  return (
    <DraggableFilterPanel
      panelId="geofence-filter"
      widgetId="geofenceFilter"
      open={open}
      onClose={onClose}
      title={t('sharedGeofences') || 'Geofences'}
      icon={<LayersIcon fontSize="small" color="action" />}
      count={`${visibleCount}/${totalCount}`}
      countColor={visibleCount === totalCount ? 'primary' : 'warning'}
      initialPosition={initialPosition || { top: WIDGET_POSITIONS.geofenceFilter.top, right: WIDGET_POSITIONS.geofenceFilter.right }}
    >
      <Box className={classes.content}>
        {/* Campo de busca */}
        <TextField
          fullWidth
          size="small"
          placeholder={t('sharedSearch') || 'Buscar...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 0.5 }}
        />

        {/* Botões de seleção */}
        <Box className={classes.actions}>
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
            onClick={handleSelectAll}
            className={classes.actionButton}
          >
            {t('sharedAll')}
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
            onClick={handleSelectNone}
            className={classes.actionButton}
          >
            {t('sharedHide')}
          </Button>
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* Lista de geofences */}
        {filteredGeofences.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="caption" color="text.secondary">
              {searchQuery
                ? (t('sharedNoResults') || 'Nenhum resultado')
                : (t('sharedNoGeofences') || 'Nenhuma geofence')}
            </Typography>
          </Box>
        ) : (
          <List dense className={classes.list}>
            {filteredGeofences.map((geofence) => (
              <ListItem key={geofence.id} dense disablePadding className={classes.listItem}>
                <FormControlLabel
                  sx={{ width: '100%', mx: 0, py: 0, px: 0.25 }}
                  control={(
                    <Checkbox
                      checked={selectedIds.has(geofence.id)}
                      onChange={() => handleToggle(geofence.id)}
                      size="small"
                      className={classes.checkbox}
                    />
                  )}
                  label={(
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box
                        className={classes.colorDot}
                        sx={{ backgroundColor: getGeofenceColor(geofence) }}
                      />
                      <Typography className={classes.geofenceName} noWrap>
                        {geofence.name || `Geofence ${geofence.id}`}
                      </Typography>
                    </Box>
                  )}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </DraggableFilterPanel>
  );
};

export default GeofenceFilterPanel;
